import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import Dropzone from 'dropzone';
import { FilesService } from 'src/app/modules/settings/services/files.service';

@Component({
  selector: 'app-upload-image-v1',
  templateUrl: './upload-image.component.html',
  styleUrl: './upload-image.component.scss'
})
export class UploadImageComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) description!: string;
  @Input({ required: false }) preloadUrl?: string;
  @Input({ required: false }) control?: FormControl;
  @Input({ required: false }) url?: string;  // Optional when manual mode
  @Input({ required: false }) uploadMode: 'auto' | 'manual' = 'auto';  // New: manual mode support

  @ViewChild('dropzoneContainer', { static: false }) dropzoneRef!: ElementRef<HTMLDivElement>;

  @Output() fileUploaded = new EventEmitter<string>();  // For auto mode
  @Output() fileSelected = new EventEmitter<File>();   // New: For manual mode
  @Output() fileRemoved = new EventEmitter<void>();    // New: For manual mode

  dropzoneInstance?: Dropzone;
  prevFile: any = null;

  mockFile: any;
  
  // Flag to track component state - prevents showing errors during cleanup/navigation
  private isDestroying: boolean = false;
  private isInitialized: boolean = false;

  ngAfterViewInit(): void {
    if (this.dropzoneRef?.nativeElement) {
      Dropzone.autoDiscover = false;
      
      // Validate: url is required in auto mode
      if (this.uploadMode === 'auto' && !this.url) {
        console.error('UploadImageComponent: url is required when uploadMode is "auto"');
        return;
      }
      
      this.initDropzone(this.dropzoneRef.nativeElement);
      this.isInitialized = true;
    } else {
      console.error("Dropzone container element not found!");
    }
  }

  constructor(private fileService: FilesService, private toastr: ToastrService) { }

  private initDropzone(selector: HTMLElement): void {
    // In manual mode, don't use file service (no auto-upload)
    if (this.uploadMode === 'manual') {
      this.initManualDropzone(selector);
    } else {
      // Auto mode: use existing file service
      if (!this.url) {
        console.error('UploadImageComponent: url is required when uploadMode is "auto"');
        return;
      }
      this.dropzoneInstance = this.fileService.initDropzone(selector, this.url);
    }

    if (this.preloadUrl) {
      this.loadPreloadedImage(this.preloadUrl);
    }

    if (this.dropzoneInstance) {
      // Auto mode handlers
      if (this.uploadMode === 'auto') {
        this.dropzoneInstance.on('success', (file: any, response: any) => {
          this.control?.setValue(response.data.fullPathe);
          this.fileUploaded.emit();
        });

        this.dropzoneInstance.on('removedfile', () => {
          this.control?.setValue('');
        });
      }

      // Manual mode handlers
      if (this.uploadMode === 'manual') {
        this.dropzoneInstance.on('addedfile', (file: any) => {
          // Skip mock files (from preload) - they're not real File objects
          if (file && !(file instanceof File) && !(file instanceof Blob) && file.name === 'Preloaded Image') {
            // This is a mock file from preload, don't emit it
            return;
          }
          
          if (this.prevFile) {
            this.dropzoneInstance?.removeFile(this.prevFile);
          }
          this.prevFile = file;
          
          // Mark file as complete immediately to remove loading indicator
          // Since we're not actually uploading, mark it as successful right away
          setTimeout(() => {
            if (this.dropzoneInstance && file) {
              // Emit complete event to remove loading bar
              // This tells Dropzone the file is "processed" even though we didn't upload
              this.dropzoneInstance.emit('complete', file);
            }
          }, 100);
          
          // Emit the File object for parent component (only real File objects)
          if (file instanceof File) {
            this.fileSelected.emit(file);
          }
        });

        this.dropzoneInstance.on('removedfile', () => {
          this.prevFile = null;
          this.fileRemoved.emit();
        });
      } else {
        // Auto mode: existing behavior
        this.dropzoneInstance.on('addedfile', (file: any) => {
          if (this.prevFile) {
            this.dropzoneInstance?.removeFile(this.prevFile);
          }
          this.prevFile = file;
        });
      }
    }
  }

  private initManualDropzone(selector: HTMLElement): void {
    // Initialize Dropzone without auto-upload
    // Use '#' as placeholder URL since we're not actually uploading
    this.dropzoneInstance = new Dropzone(selector, {
      url: '#',  // Placeholder URL (required by Dropzone, but won't be used)
      autoProcessQueue: false,  // Don't auto-upload
      headers: { "Authorization": "Bearer " + sessionStorage.getItem('accessToken') },
      paramName: "File",
      maxFiles: 1,
      maxFilesize: 10,
      addRemoveLinks: true,
      acceptedFiles: 'image/*',
      // Accept files immediately to prevent processing state
      accept: (file: any, done: Function) => {
        // Accept the file immediately (no validation needed in manual mode)
        done();
      },
      error: (file, error: any, xhr) => {
        // Only show errors if:
        // 1. Component is not being destroyed
        // 2. Component is initialized
        // 3. Error is user-relevant (not a cleanup/navigation error)
        if (this.isDestroying || !this.isInitialized) {
          // Silently ignore errors during cleanup or before initialization
          return;
        }
        
        // Check if this is a user-relevant error (file validation, size, type)
        const errorMessage = error?.message || '';
        const errorString = String(error || '');
        
        // Ignore common cleanup/navigation errors
        const ignoredErrors = [
          'abort',
          'cancel',
          'destroy',
          'navigation',
          'component',
          'cleanup',
          '#',
          'placeholder'
        ];
        
        const isIgnoredError = ignoredErrors.some(ignored => 
          errorMessage.toLowerCase().includes(ignored) || 
          errorString.toLowerCase().includes(ignored)
        );
        
        // Also ignore errors related to placeholder URL in manual mode
        if (this.uploadMode === 'manual' && (errorMessage.includes('#') || !file)) {
          return;
        }
        
        // Only show real user-facing errors
        if (!isIgnoredError && file && file instanceof File) {
          // This is a real file validation error - show it to the user
          this.toastr.error(
            errorMessage || 'Error selecting the image',
            'Error'
          );
        }
        // Otherwise, silently ignore (cleanup/navigation errors)
      }
    });
  }

  private loadPreloadedImage(preloadUrl?: string): void {
    if (this.dropzoneInstance) {
      if (this.mockFile) {
        this.dropzoneInstance.emit("removedfile", this.mockFile);
      }

      if (!preloadUrl) {
        return;
      }

      this.mockFile = {
        name: 'Preloaded Image',
        size: 12345,
        type: 'image/jpeg',
      };

      this.dropzoneInstance.emit('addedfile', this.mockFile);
      this.dropzoneInstance.emit('thumbnail', this.mockFile, preloadUrl);
      this.dropzoneInstance.emit('complete', this.mockFile);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['preloadUrl'] && !changes['preloadUrl'].firstChange) {
      this.loadPreloadedImage(this.preloadUrl);
    }
  }

  ngOnDestroy() {
    // Set flag to prevent error handlers from firing during cleanup
    this.isDestroying = true;
    
    // Remove all event listeners before destroying to prevent errors
    if (this.dropzoneInstance) {
      try {
        // Remove all event listeners
        this.dropzoneInstance.off();
        
        // Remove all files before destroying
        if (this.dropzoneInstance.files && this.dropzoneInstance.files.length > 0) {
          this.dropzoneInstance.files.forEach((file: any) => {
            try {
              this.dropzoneInstance?.removeFile(file);
            } catch (e) {
              // Ignore errors during cleanup
            }
          });
        }
        
        // Destroy the instance
        this.dropzoneInstance.destroy();
      } catch (error) {
        // Silently ignore any errors during cleanup
        console.debug('UploadImageComponent: Error during Dropzone cleanup (ignored)', error);
      }
    }
    
    this.dropzoneInstance = undefined;
    this.prevFile = null;
    this.mockFile = null;
  }
}
